import style from "./data-layer-menu.module.css";
import { useEffect } from "react";
import { Channel } from "pusher-js";
import ElementDataEditor from "./element-data-editor/element-data-editor";
import {
  FloatingFocusManager,
  useFloating,
  autoUpdate,
  flip,
  shift,
  useDismiss,
  useClientPoint,
  useInteractions,
  useTransitionStyles,
  offset,
} from "@floating-ui/react";
import { IntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import ElementRulesEditor from "./element-rules-editor/element-rules-editor";
import { syncServiceAtom } from "state-atoms";
import { useCanvasElementById } from "subscriptions";
import { useAtomValue } from "jotai";
import { MondayItemManagerProvider } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";

export interface DataLayerMenuProps {
  documentId?: string;
  pusherChannel?: Channel | null;
  anchor: DOMRectReadOnly;
  elementId: string;
  onDismiss: () => void;
}

function BaseDataLayerMenu({
  anchor,
  onDismiss,
  documentId,
  pusherChannel,
  children,
}: DataLayerMenuProps & { children: React.ReactNode }) {
  const { refs, floatingStyles, context } = useFloating({
    placement: "left",
    open: true,
    onOpenChange: onDismiss,
    whileElementsMounted: autoUpdate, // autoUpdate is necessary since the popup can change height
    middleware: [
      offset(20),
      flip({ padding: 20 }),
      shift({ crossAxis: true, boundary: document.getElementById("no-controls-area") ?? undefined }),
    ],
  });

  useEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect() {
        return anchor;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  const dismiss = useDismiss(context);
  const clientPoint = useClientPoint(context, { enabled: true, axis: "both", x: anchor.x, y: anchor.y });

  // Merge all the interactions into prop getters
  const { getFloatingProps } = useInteractions([clientPoint, dismiss]);
  const { styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
  });

  return (
    <IntegrationsProvider documentId={documentId} pusherChannel={pusherChannel}>
      <MondayItemManagerProvider documentId={documentId}>
        <FloatingFocusManager context={context} returnFocus={true} restoreFocus={true}>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 100 }} {...getFloatingProps()}>
            <div className={style.container} style={styles}>
              {children}
            </div>
          </div>
        </FloatingFocusManager>
      </MondayItemManagerProvider>
    </IntegrationsProvider>
  );
}

export function DataEditorMenu(props: DataLayerMenuProps) {
  const syncService = useAtomValue(syncServiceAtom);
  const element = useCanvasElementById(syncService?.getReplicache(), props.elementId, null, [props.elementId]);
  return (
    <BaseDataLayerMenu {...props}>
      <ElementDataEditor elementId={props.elementId} element={element} />
    </BaseDataLayerMenu>
  );
}

export function RulesEditorMenu(props: DataLayerMenuProps) {
  const syncService = useAtomValue(syncServiceAtom);
  const element = useCanvasElementById(syncService?.getReplicache(), props.elementId, null, [props.elementId]);
  return (
    <BaseDataLayerMenu {...props}>
      <ElementRulesEditor elementId={props.elementId} element={element} />
    </BaseDataLayerMenu>
  );
}
